import React, { useCallback } from 'react';

import { IconsEnum } from '../../../../../assets/icons/types';
import { IsDisabled } from '../../../../../types';
import { FetchProductsFilterFields } from '../../../../products/productsTypes';

import { ProductsAiSearchOnClipGlobalSearch } from '../../../../products/components/containers/ProductsAiSearch';
import { ProductsAiSearchClipGlobalModalButton } from '../../../../products/components/containers/ProductsAiSearch/components/ProductsAiSearchClipGlobalModalButton';
import { ProductsAiSearchByLSGlobalModalButton } from '../../../../products/components/containers/ProductsAiSearch/components/ProductsAiSearchByLSGlobalModalButton';

import { CacheProductsSearchSuggestInput } from '../../../../products/helpers/CacheProductsSearchSuggestInput';
import { TextFilter } from '../../../../../helpers/filters/TextFilter';

import { aiSearchKeys, productsKeys } from '../../../../../locales/keys';

interface ThreeDStockProductsSearchFieldProps {
  withSuggestInput?: boolean;
  withSimilarImageFilter?: boolean;
  withSimilarInImageFilter?: boolean;
  value?: string;
  onSearch?: (value: string) => void;
  onSearchSimilarImage?: (imageUrl: string) => void;
  onSearchInImage?: (imageUrl: string) => void;
  disabled?: IsDisabled;
}

function ThreeDStockProductsSearchField({
  withSuggestInput,
  withSimilarImageFilter,
  withSimilarInImageFilter,
  value = '',
  onSearch,
  onSearchSimilarImage,
  onSearchInImage,
  disabled
}: ThreeDStockProductsSearchFieldProps) {
  const handleSearch = useCallback<
    (
      changedFilters: { [nameSkuTags: string]: string },
      removeFilters: string[]
    ) => void
  >(
    ({ nameSkuTags }) => {
      onSearch(nameSkuTags);
    },
    [onSearch]
  );

  const handleSearchSimilarImage =
    useCallback<ProductsAiSearchOnClipGlobalSearch>(
      (data) => {
        onSearchSimilarImage(data.fileAttachment?.file);
      },
      [onSearchSimilarImage]
    );

  const handleSearchInImage = useCallback<ProductsAiSearchOnClipGlobalSearch>(
    (data) => {
      onSearchInImage(data.fileAttachment?.file);
    },
    [onSearchInImage]
  );

  return (
    <div className="relative grow flex-1 py-2">
      {withSuggestInput ? (
        <CacheProductsSearchSuggestInput
          dataGa="three-d-stock-products-text-filter-input"
          value={value}
          onSubmit={onSearch}
          icon={IconsEnum.SEARCH_OUTLINE}
          iconClassName="stroke-2 h-6 w-6 shrink-0 pointer-events-none"
          iconWrapperClassName="absolute left-4 top-3"
          inputClassName="pl-12 pr-24 flex items-center gap-2 h-full w-full border rounded-full shadow-inner bg-gray-100 border-gray-500 dark:bg-gray-950 dark:border-gray-500 hover:border-gray-900 dark:hover:border-blue-200 focus-within:hover:border-blue-300 focus-within:ring-primary transition-colors duration-150 ease-out"
          popoverClassName="overflow-auto max-h-80 bg-white bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 flex flex-col pointer-events-auto rounded-3xl dark:glow-lg shadow-xl w-full"
          disabled={disabled}
        />
      ) : (
        <TextFilter
          changeOnEnter
          dataGa="three-d-stock-products-text-filter-input"
          disabled={disabled}
          i18nPlaceholder={productsKeys.search}
          icon={IconsEnum.SEARCH_OUTLINE}
          iconClassName="stroke-2 h-6 w-6 shrink-0 pointer-events-none"
          iconWrapperClassName="absolute left-4 top-3"
          inputClassName="pl-12 pr-24 flex items-center gap-2 h-full w-full border rounded-full shadow-inner bg-gray-100 border-gray-500 dark:bg-gray-950 dark:border-gray-500 hover:border-gray-900 dark:hover:border-blue-200 focus-within:hover:border-blue-300 focus-within:ring-primary transition-colors duration-150 ease-out"
          className="h-full w-full relative"
          name={FetchProductsFilterFields.NAME_SKU_TAGS}
          onChange={handleSearch}
          value={value}
          type="text"
        />
      )}
      <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex border-l border-gray-300 dark:border-gray-700 pl-2">
        {withSimilarImageFilter && (
          <ProductsAiSearchClipGlobalModalButton
            className="py-1.5 pl-1.5 pr-1.5 sm:pr-3 sm:space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 transition-colors duration-150 ease-out"
            dataGa="three-d-stock-products-clip-global-search-filter-button"
            i18nText={aiSearchKeys.imageSearch}
            i18nTextClassName="sr-only sm:not-sr-only normal-case"
            i18nTitle={aiSearchKeys.imageSearch}
            icon={IconsEnum.PHOTO_AI_SOLID}
            iconClassName="h-5 w-5"
            onSubmit={handleSearchSimilarImage}
            disabled={disabled}
          />
        )}

        {withSimilarInImageFilter && (
          <ProductsAiSearchByLSGlobalModalButton
            className="py-1.5 pl-1.5 pr-1.5 sm:pr-3 sm:space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 transition-colors duration-150 ease-out"
            dataGa="three-d-stock-products-clip-global-search-filter-button"
            i18nText={aiSearchKeys.searchInImage}
            i18nTextClassName="sr-only sm:not-sr-only normal-case"
            i18nTitle={aiSearchKeys.searchInImage}
            icon={IconsEnum.PHOTO_AI_SOLID}
            iconClassName="h-5 w-5"
            onSubmit={handleSearchInImage}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
}

export default ThreeDStockProductsSearchField;
